import { type FC, type ReactNode, useContext } from "react";
import { useDrop } from "react-dnd";
import { DragDropEditorContext, type DragDropItem, isDragDropNew } from "./DragDropTypes";

interface Props {
    id: string;
    count: number;
    children: ReactNode;
}

export const DragDropContainerWrapper: FC<Props> = ({
    id,
    count,
    children,
}) => {
    const { accept, onCreate, manager } = useContext(DragDropEditorContext);

    // This only handles dropping items in the empty space below the components in the container
    const [{ isOver }, drop ] = useDrop<DragDropItem, void, { isOver: boolean; }>(() => ({
        accept,
        collect: monitor => ({
            isOver: monitor.isOver({ shallow: true }),
        }),
        drop: (item, monitor) => {
            if(monitor.isOver({ shallow: true })) {
                let detached: string | null;
                let newIndex = count;

                if(isDragDropNew(item)) {
                    detached = manager.registerComponent(onCreate(item.type));
                } else {
                    detached = manager.detachComponent(item.container, item.index);

                    // Check if the item is being moved below itself in the same container
                    if(item.container === id) {
                        // Fix the detaching of the dragging object changing the correct index to move to
                        newIndex--;
                    }
                }

                if(!detached) {
                    return;
                }

                manager.attachComponent(id, detached, newIndex);
            }
        },
    }), [ accept, onCreate, count ]);

    return (
        <div
            ref={el => {
                drop(el);
            }}
            className="min-h-full"
        >
            {children}
            {isOver && (
                <div className="h-0 z-10 relative">
                    <div className="border-t-[4px] border-t-blue-400 -mt-[2px] w-full" />
                </div>
            )}
        </div>
    );
};
